import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Ruler } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { formatPrice } from "../data/products";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CartDrawer from "../components/CartDrawer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import ProductDetail from "../components/ProductDetail";

const ProductsPage = () => {
  const { lang, t, addToCart, products, productMedia, productVideo, options, seriesLabel } = useShop();
  const [finish, setFinish] = useState("all");
  const [size, setSize] = useState("all");
  const [sort, setSort] = useState("featured");
  const [selectedDims, setSelectedDims] = useState({});
  const [detailProduct, setDetailProduct] = useState(null);
  const d = t.catalog;

  const all = products || [];
  const seriesOpts = options?.series || [];
  const dimsList = options?.dims || [];

  const dimsOf = (p) => (p.available_dims?.length ? p.available_dims : [p.dims].filter(Boolean));
  const chosenDims = (p) => selectedDims[p.id] || dimsOf(p)[0] || p.dims;

  const list = useMemo(() => {
    let out = all.filter(
      (p) =>
        (finish === "all" || p.series === finish) &&
        (size === "all" || dimsOf(p).includes(size) || p.dims === size)
    );
    if (sort === "priceAsc") out = [...out].sort((a, b) => a.price - b.price);
    if (sort === "priceDesc") out = [...out].sort((a, b) => b.price - a.price);
    return out;
  }, [all, finish, size, sort]);

  const handleAdd = (product) => {
    const dims = chosenDims(product);
    addToCart(product.id, dims);
    toast.success(`${product.name[lang]} (${dims}) — ${t.products.added}`);
  };

  const Chip = ({ active, onClick, testid, children }) => (
    <button
      onClick={onClick}
      className={`px-5 py-2 rounded-full text-xs font-medium tracking-widest uppercase transition-colors duration-300 border ${
        active
          ? "bg-brown text-cream border-brown"
          : "border-brown/20 text-brown-light hover:border-olive hover:text-olive-dark"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );

  return (
    <div className="bg-cream text-brown font-sans min-h-screen" data-testid="products-page">
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar />

      <main className="pt-36 md:pt-44 pb-24 md:pb-36 px-6 md:px-12 max-w-[1440px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-olive-dark mb-4">
              {d.overline}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-brown">
              {d.title}
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-brown-light">
              {d.sub}
            </p>
          </div>
          <p className="text-sm text-brown-muted whitespace-nowrap" data-testid="results-count">
            {list.length} {d.results}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 flex flex-col lg:flex-row lg:items-center gap-8 border-y border-brown/10 py-6"
          data-testid="catalog-filters"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] tracking-[0.25em] uppercase text-brown-muted mr-1">
              {d.filterFinish}
            </span>
            <Chip active={finish === "all"} onClick={() => setFinish("all")} testid="filter-finish-all">
              {d.all}
            </Chip>
            {seriesOpts.map((o) => (
              <Chip
                key={o.key}
                active={finish === o.key}
                onClick={() => setFinish(o.key)}
                testid={`filter-finish-${o.key}`}
              >
                {lang === "tr" ? o.label_tr : o.label_en}
              </Chip>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] tracking-[0.25em] uppercase text-brown-muted mr-1">
              {d.filterSize}
            </span>
            <Chip active={size === "all"} onClick={() => setSize("all")} testid="filter-size-all">
              {d.all}
            </Chip>
            {dimsList.map((dims) => (
              <Chip
                key={dims}
                active={size === dims}
                onClick={() => setSize(dims)}
                testid={`filter-size-${dims.replace(/[^0-9]/g, "-")}`}
              >
                {dims}
              </Chip>
            ))}
          </div>

          <div className="lg:ml-auto flex items-center gap-3">
            <span className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
              {d.sortLabel}
            </span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent border border-brown/20 rounded-full px-5 py-2 text-sm text-brown focus:outline-none focus:border-olive cursor-pointer"
              data-testid="sort-select"
            >
              <option value="featured">{d.sortFeatured}</option>
              <option value="priceAsc">{d.sortPriceAsc}</option>
              <option value="priceDesc">{d.sortPriceDesc}</option>
            </select>
          </div>
        </motion.div>

        {products === null ? (
          <div className="py-24 text-center">
            <p className="text-brown-muted font-light">{t.auth.loading}</p>
          </div>
        ) : list.length === 0 ? (
          <div className="py-24 text-center" data-testid="catalog-empty">
            <p className="font-serif text-2xl text-brown-muted italic">{d.noResults}</p>
          </div>
        ) : (
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-20" data-testid="catalog-grid">
            {list.map((product, i) => {
              const video = productVideo(product);
              const photo = productMedia(product);
              const sizes = dimsOf(product);
              const chosen = chosenDims(product);
              return (
                <motion.article
                  key={product.id}
                  initial={{ opacity: 0, y: 48 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  className="group"
                  data-testid={`catalog-card-${product.id}`}
                >
                  <div
                    className="relative overflow-hidden aspect-[4/3] bg-cream-dark cursor-pointer"
                    onClick={() => setDetailProduct(product)}
                    data-testid={`catalog-open-${product.id}`}
                  >
                    {video ? (
                      <video
                        src={video}
                        poster={photo || undefined}
                        controls
                        preload="metadata"
                        className="w-full h-full object-cover"
                        data-testid={`catalog-video-${product.id}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <img
                        src={photo}
                        alt={`${product.name[lang]} — ${seriesLabel(product.series)} PVC kombi dolabı`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-brown/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    <span className="absolute top-5 left-5 px-3 py-1 bg-cream/90 backdrop-blur text-[10px] tracking-[0.2em] uppercase font-semibold text-brown">
                      {seriesLabel(product.series)}
                    </span>
                    {product.bestseller && (
                      <span className="absolute top-5 right-5 px-3 py-1 bg-olive text-cream text-[10px] tracking-[0.2em] uppercase font-semibold">
                        {t.products.bestseller}
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAdd(product);
                      }}
                      className="absolute bottom-5 right-5 w-12 h-12 rounded-full bg-cream text-brown flex items-center justify-center opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400 hover:bg-olive hover:text-cream"
                      aria-label={t.products.addToCart}
                      data-testid={`catalog-quick-add-${product.id}`}
                    >
                      <Plus size={20} />
                    </button>
                  </div>

                  <div className="pt-8 flex items-start justify-between gap-6">
                    <div className="min-w-0">
                      <h2
                        className="font-serif text-2xl sm:text-3xl font-medium text-brown cursor-pointer hover:text-olive-dark transition-colors duration-300"
                        onClick={() => setDetailProduct(product)}
                      >
                        {product.name[lang]}
                      </h2>
                      {product.description?.[lang] && (
                        <p className="mt-3 text-sm font-light text-brown-light leading-relaxed line-clamp-2 max-w-md">
                          {product.description[lang]}
                        </p>
                      )}
                      <p className="mt-3 text-lg font-medium text-brown" data-testid={`catalog-price-${product.id}`}>
                        {formatPrice(product.price)}
                        <span className="ml-1.5 text-xs font-light text-brown-muted">
                          {t.products.from}
                        </span>
                      </p>

                      <div className="mt-4 flex flex-wrap gap-2" data-testid={`catalog-sizes-${product.id}`}>
                        {sizes.map((dims) => (
                          <button
                            key={dims}
                            onClick={() => setSelectedDims((s) => ({ ...s, [product.id]: dims }))}
                            className={`px-4 py-1.5 rounded-full text-[11px] font-medium tracking-wide transition-colors duration-300 border ${
                              chosen === dims
                                ? "bg-brown text-cream border-brown"
                                : "border-brown/20 text-brown-light hover:border-olive hover:text-olive-dark"
                            }`}
                            data-testid={`catalog-size-${product.id}-${dims.replace(/[^0-9]/g, "-")}`}
                          >
                            {dims}
                          </button>
                        ))}
                      </div>

                      <p
                        className="mt-3 inline-flex items-center gap-2 text-xs tracking-wide text-olive-dark"
                        data-testid={`catalog-custom-note-${product.id}`}
                      >
                        <Ruler size={13} strokeWidth={1.75} /> {d.customNote}
                      </p>
                    </div>
                    <button
                      onClick={() => handleAdd(product)}
                      className="mt-1 shrink-0 px-6 py-3 rounded-full bg-olive text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors duration-300"
                      data-testid={`catalog-add-${product.id}`}
                    >
                      {t.products.addToCart}
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </main>

      <ProductDetail
        product={detailProduct}
        open={Boolean(detailProduct)}
        onOpenChange={(open) => !open && setDetailProduct(null)}
      />
      <Footer />
      <CartDrawer />
      <WhatsAppFloat />
    </div>
  );
};

export default ProductsPage;
